import { useRef } from 'react';
import BudgetCurrencyInput from '../BudgetCurrencyInput';
import { IBudgetItem } from '../BudgetItemsTable';
import BudgetDescriptionSelect from '../BudgetDescriptionSelect';
import BudgetPercentageInput from '../BudgetPercentageInput';
import BudgetQuantityInput from '../BudgetQuantityInput';
import { IBudgetServiceItemOption } from '../../../../../api/fetchBudget';
import { parseDiscountPercent, parseQuantityToNumber } from '../../utils/budgetCalculations';

interface IBudgetTableRowMobileProps {
  item: IBudgetItem;
  originalItem?: IBudgetItem;
  index: number;
  isExternalService?: boolean;
  isEditMode?: boolean;
  onItemChange?: (index: number, field: keyof IBudgetItem, value: string | number) => void;
  onRemoveItem?: (index: number) => void;
}

const BudgetTableRowMobile = (props: IBudgetTableRowMobileProps) => {
  const isRemoved = props.item.removed === true;
  const originalItem = props.originalItem;
  const valueInputRef = useRef<HTMLInputElement>(null);

  const currentQuantity = String(props.item.quantity || '').trim();
  const originalQuantityStr = originalItem ? String(originalItem.quantity || '').trim() : '';
  const quantityChanged = originalItem && originalQuantityStr !== '' && originalQuantityStr !== currentQuantity;
  const originalQuantity = originalItem?.quantity;

  const isQuantityZero = parseQuantityToNumber(currentQuantity) === 0;
  const shouldShowRemovedStyle = isRemoved || isQuantityZero;

  const currentValue = typeof props.item.value === 'number' ? props.item.value : parseFloat(String(props.item.value || 0)) || 0;
  const originalValueNum = originalItem 
    ? (typeof originalItem.value === 'number' ? originalItem.value : parseFloat(String(originalItem.value || 0)) || 0)
    : 0;
  const valueChanged = originalItem && originalValueNum !== 0 && Math.abs(originalValueNum - currentValue) > 0.01;
  const originalValue = originalItem?.value;

  const formatOriginalValue = (value: number | undefined): string => {
    if (value === undefined || value === null) return '';
    const reais = Math.floor(value);
    const centavos = Math.round((value - reais) * 100);
    const formattedReais = reais.toLocaleString('pt-BR');
    const formattedCentavos = String(centavos).padStart(2, '0');
    return `R$ ${formattedReais},${formattedCentavos}`;
  };

  const handleDescriptionChange = (option: { id: string; label: string; value?: number; itemData?: IBudgetServiceItemOption } | undefined) => {
    if (!isRemoved && !isQuantityZero && option) {
      const description = option.id || option.label.split(' (')[0] || '';
      props.onItemChange?.(props.index, 'description', description);

      if (option.itemData) {
        const itemData = option.itemData;
        const hasValue = option.value && option.value !== 0;
        const hasDiscount = props.item.discount && props.item.discount !== '0%' && props.item.discount !== '00%';

        if (itemData.item) {
          props.onItemChange?.(props.index, 'item', itemData.item);
        }

        if (itemData.quantity !== undefined && itemData.quantity !== null && itemData.quantity > 0) {
          const quantityStr = String(itemData.quantity).padStart(2, '0');
          props.onItemChange?.(props.index, 'quantity', quantityStr);
        } else if (itemData.quantity === 0 || itemData.quantity === null) {
          props.onItemChange?.(props.index, 'quantity', '01');
        }
        
        if (hasValue || hasDiscount) {
          props.onItemChange?.(props.index, 'value', option.value || 0);
          props.onItemChange?.(props.index, 'discount', '00%');
          
          setTimeout(() => {
            if (valueInputRef.current) {
              valueInputRef.current.focus();
            }
          }, 100);
        }
      }
    }
  };

  return (
    <div className={`border border-gray-200 rounded-md p-3 ${shouldShowRemovedStyle ? 'bg-red-50' : 'bg-white'}`}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-row justify-between items-start">
          <div className="flex-1">
            <div className="text-size-12 text-gray-600 mb-1">Ref.</div>
            <div className="text-size-14 font-semibold">{props.item.ref}</div>
          </div>
          <div className="flex-1 ml-2">
            <div className="text-size-12 text-gray-600 mb-1">Item</div>
            <input
              type="text"
              className={`form-control w-full ${shouldShowRemovedStyle ? '!bg-red-50' : ''}`}
              value={props.item.item || ''}
              onChange={(e) => {
                if (!isRemoved && !isQuantityZero) {
                  props.onItemChange?.(props.index, 'item', e.target.value);
                }
              }}
              disabled={shouldShowRemovedStyle}
              style={shouldShowRemovedStyle ? { backgroundColor: '#fef2f2' } : undefined}
            />
          </div>
          <div className="ml-2">
            <button
              type="button"
              className={`cursor-pointer bg-transparent border-none p-2 ${
                shouldShowRemovedStyle ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={() => {
                if (!isRemoved && !isQuantityZero) {
                  props.onRemoveItem?.(props.index);
                }
              }}
              disabled={shouldShowRemovedStyle}
            >
              <img src="/assets/images/bin.svg" alt="Excluir item" className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        <div>
          <div className="text-size-12 text-gray-600 mb-1">Descrição</div>
          {props.isExternalService ? (
            <input
              type="text"
              className={`form-control w-full ${shouldShowRemovedStyle ? '!bg-red-50' : ''}`}
              value={props.item.description || ''}
              onChange={(e) => {
                if (!isRemoved && !isQuantityZero) {
                  props.onItemChange?.(props.index, 'description', e.target.value);
                }
              }}
              disabled={shouldShowRemovedStyle}
              style={shouldShowRemovedStyle ? { backgroundColor: '#fef2f2' } : undefined}
              placeholder="Descrição"
            />
          ) : (
            <BudgetDescriptionSelect
              value={props.item.description || ''}
              onChange={handleDescriptionChange}
              disabled={shouldShowRemovedStyle}
              isRemoved={shouldShowRemovedStyle}
              autoFocus={false}
            />
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-size-12 text-gray-600 mb-1">Quantidade</div>
            <div className="relative">
              <BudgetQuantityInput
                value={shouldShowRemovedStyle && originalQuantity ? originalQuantity : props.item.quantity}
                onChange={(value) => {
                  if (!isRemoved && !isQuantityZero) {
                    props.onItemChange?.(props.index, 'quantity', value);
                  }
                }}
                disabled={shouldShowRemovedStyle}
                isRemoved={shouldShowRemovedStyle}
                isChanged={quantityChanged && !shouldShowRemovedStyle}
              />
              {quantityChanged && originalQuantity && !shouldShowRemovedStyle && (
                <span className="text-size-11 text-gray-500 mt-1 block">Anterior ({originalQuantity})</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-size-12 text-gray-600 mb-1">Valor (R$)</div>
            <div className="relative">
              <BudgetCurrencyInput
                ref={valueInputRef}
                value={props.item.value}
                onChange={(value) => {
                  if (!isRemoved && !isQuantityZero) {
                    props.onItemChange?.(props.index, 'value', value);
                  }
                }}
                disabled={shouldShowRemovedStyle}
                isRemoved={shouldShowRemovedStyle}
                isChanged={valueChanged}
              />
              {valueChanged && originalValue !== undefined && (
                <span className="text-size-11 text-gray-500 mt-1 block">Anterior ({formatOriginalValue(originalValue)})</span>
              )}
            </div>
          </div>
          <div>
            <div className="text-size-12 text-gray-600 mb-1">% Desconto</div>
            <BudgetPercentageInput
              value={props.item.discount}
              onChange={(value) => {
                if (!isRemoved && !isQuantityZero) {
                  const discountValue =
                    value % 1 === 0
                      ? `${String(value).padStart(2, '0')}%`
                      : `${value.toFixed(2).replace('.', ',')}%`;
                  props.onItemChange?.(props.index, 'discount', discountValue);
                }
              }}
              disabled={shouldShowRemovedStyle}
              isRemoved={shouldShowRemovedStyle}
            />
          </div>
          <div>
            <div className="text-size-12 text-gray-600 mb-1">Subtotal (R$)</div>
            <BudgetCurrencyInput
              value={parseQuantityToNumber(props.item.quantity) * props.item.value * (1 - parseDiscountPercent(props.item.discount) / 100)}
              onChange={(value) => props.onItemChange?.(props.index, 'subtotal', value)}
              disabled={true}
              isRemoved={shouldShowRemovedStyle}
            />
          </div>
        </div>

        {shouldShowRemovedStyle && (
          <div className="text-red-400 text-size-12 font-bold mt-2">
            Removido
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetTableRowMobile;
